import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Descriptions,
	Input,
	Modal,
	Space,
	Tag,
	Typography,
} from 'antd';
import { MailOutlined, SearchOutlined } from '@ant-design/icons';
import { sendToast } from 'screens/Dashboard/actions';
import { ApiServiceV2 } from 'services';
import {
	MAX_ADMIN_EMAIL_LENGTH,
	MAX_CONTRACTOR_IDC_LENGTH,
	buildAcceptedOnboardingMessage,
	canChangeAdminEmailAndReissue,
	getOnboardingEligibilityMessage,
	getOnboardingMutationError,
	isAdminOnboardingEligible,
	isValidAdminOnboardingReissueResponse,
	normalizeAdminEmail,
	normalizeContractorIdc,
	validateAdminEmailChange,
} from './tenantAdminOnboarding';

const { Paragraph, Text } = Typography;

const LOOKUP_URL = '/contractors/v1/admin-onboarding/lookup';

const getLookupError = () =>
	'No fue posible consultar la contrata con la cédula indicada.';

const getOnboardingStatusColor = status => {
	if (status === 'sent' || status === 'used') return 'green';
	if (status === 'expired' || status === 'revoked') return 'red';
	return 'gold';
};

export default function TenantAdminOnboardingCard() {
	const dispatch = useDispatch();
	const lookupAttemptRef = useRef(0);
	const lookupSubmittingRef = useRef(null);
	const reissueSubmittingRef = useRef(false);
	const logicalAttemptRef = useRef(null);
	const [contractorIdc, setContractorIdc] = useState('');
	const [lookup, setLookup] = useState({
		isSearching: false,
		result: null,
		error: null,
	});
	const [modal, setModal] = useState({
		visible: false,
		newEmail: '',
		confirmed: false,
		isSubmitting: false,
		error: null,
	});

	const clearLogicalAttempt = () => {
		logicalAttemptRef.current = null;
	};

	const onContractorIdcChange = event => {
		lookupAttemptRef.current += 1;
		lookupSubmittingRef.current = null;
		setContractorIdc(event.target.value);
		setLookup({ isSearching: false, result: null, error: null });
		clearLogicalAttempt();
	};

	const searchContractor = async () => {
		const exactContractorIdc = normalizeContractorIdc(contractorIdc);
		if (!exactContractorIdc || lookupSubmittingRef.current !== null) return;

		const attemptId = lookupAttemptRef.current + 1;
		lookupAttemptRef.current = attemptId;
		lookupSubmittingRef.current = attemptId;
		setLookup({ isSearching: true, result: null, error: null });
		try {
			const result = await ApiServiceV2.get({
				url: LOOKUP_URL,
				params: { contractorIdc: exactContractorIdc },
			});

			if (
				!result?.contractor ||
				normalizeContractorIdc(result.contractor.idc) !== exactContractorIdc
			) {
				throw new Error('Unexpected contractor lookup response');
			}

			if (lookupAttemptRef.current === attemptId) {
				setLookup({ isSearching: false, result, error: null });
				clearLogicalAttempt();
			}
		} catch {
			if (lookupAttemptRef.current === attemptId) {
				setLookup({
					isSearching: false,
					result: null,
					error: getLookupError(),
				});
			}
		} finally {
			if (lookupSubmittingRef.current === attemptId) {
				lookupSubmittingRef.current = null;
			}
		}
	};

	const openModal = () => {
		if (
			!canChangeAdminEmailAndReissue(lookup.result) ||
			!isAdminOnboardingEligible(lookup.result)
		) {
			return;
		}
		setModal({
			visible: true,
			newEmail: '',
			confirmed: false,
			isSubmitting: false,
			error: null,
		});
	};

	const closeModal = () => {
		if (reissueSubmittingRef.current) return;
		setModal({
			visible: false,
			newEmail: '',
			confirmed: false,
			isSubmitting: false,
			error: null,
		});
	};

	const onNewEmailChange = event => {
		const nextEmail = event.target.value;
		if (
			normalizeAdminEmail(nextEmail) !== normalizeAdminEmail(modal.newEmail)
		) {
			clearLogicalAttempt();
		}
		setModal(current => ({
			...current,
			newEmail: nextEmail,
			error: null,
		}));
	};

	const submitReissue = async () => {
		if (reissueSubmittingRef.current) return;

		const result = lookup.result;
		const currentEmail = normalizeAdminEmail(result?.admin?.primaryEmail);
		const newEmail = normalizeAdminEmail(modal.newEmail);
		const validationError = validateAdminEmailChange({
			currentEmail,
			newEmail,
		});
		if (
			validationError ||
			modal.confirmed !== true ||
			!canChangeAdminEmailAndReissue(result) ||
			!isAdminOnboardingEligible(result) ||
			!result?.contractor?.entityId ||
			!result?.admin?.userId
		) {
			setModal(current => ({
				...current,
				error:
					validationError || 'No fue posible validar la operación solicitada.',
			}));
			return;
		}

		const requestData = {
			adminUserId: result.admin.userId,
			newEmail,
			expectedCurrentEmail: currentEmail,
		};
		const attemptSignature = JSON.stringify({
			contractorEntityId: result.contractor.entityId,
			...requestData,
		});
		if (logicalAttemptRef.current?.signature !== attemptSignature) {
			logicalAttemptRef.current = {
				signature: attemptSignature,
				key: uuidv4(),
			};
		}

		reissueSubmittingRef.current = true;
		setModal(current => ({
			...current,
			isSubmitting: true,
			error: null,
		}));
		try {
			const response = await ApiServiceV2.post({
				url: `/contractors/v1/${result.contractor.entityId}/admin-onboarding/reissue`,
				data: requestData,
				headers: {
					'Idempotency-Key': logicalAttemptRef.current.key,
				},
			});
			if (
				!isValidAdminOnboardingReissueResponse({
					response,
					requestedEmail: newEmail,
				})
			) {
				throw new Error('Unexpected admin onboarding reissue response');
			}
			const recipient = response.recipient;

			setLookup(current => ({
				...current,
				result: {
					...current.result,
					admin: {
						...current.result.admin,
						primaryEmail: recipient,
					},
					onboarding: {
						...current.result.onboarding,
						status: response?.status || 'accepted',
						lastDeliveryStatus: response?.status || 'accepted',
					},
				},
			}));
			setModal({
				visible: false,
				newEmail: '',
				confirmed: false,
				isSubmitting: false,
				error: null,
			});
			clearLogicalAttempt();
			dispatch(
				sendToast({
					message: buildAcceptedOnboardingMessage(recipient),
					type: 'success',
				}),
			);
		} catch (error) {
			if (error?.httpStatus === 409) {
				clearLogicalAttempt();
				setLookup({
					isSearching: false,
					result: null,
					error: getOnboardingMutationError(error),
				});
				setModal({
					visible: false,
					newEmail: '',
					confirmed: false,
					isSubmitting: false,
					error: null,
				});
				return;
			}
			if (error?.httpStatus === 403 || error?.httpStatus === 404) {
				setLookup(current => ({
					...current,
					result: current.result
						? {
								...current.result,
								capabilities: {
									...current.result.capabilities,
									canChangeAdminEmailAndReissue: false,
								},
							}
						: null,
				}));
			}
			setModal(current => ({
				...current,
				isSubmitting: false,
				error: getOnboardingMutationError(error),
			}));
		} finally {
			reissueSubmittingRef.current = false;
		}
	};

	const result = lookup.result;
	const currentEmail = result?.admin?.primaryEmail || '';
	const emailValidationError = validateAdminEmailChange({
		currentEmail,
		newEmail: modal.newEmail,
	});
	const canRenderAction = canChangeAdminEmailAndReissue(result);
	const isEligible = isAdminOnboardingEligible(result);
	const normalizedNewEmail = normalizeAdminEmail(modal.newEmail);

	return (
		<Card
			title='Onboarding de administrador de contrata'
			bordered={false}
			style={{ marginTop: 16 }}
		>
			<Space direction='vertical' size='middle' style={{ width: '100%' }}>
				<Paragraph type='secondary' style={{ marginBottom: 0 }}>
					Consulta una contrata por su cédula exacta antes de cambiar el correo
					primario y solicitar un nuevo envío de onboarding.
				</Paragraph>
				<Space wrap style={{ width: '100%' }}>
					<Input
						aria-label='Cédula exacta de la contrata'
						value={contractorIdc}
						maxLength={MAX_CONTRACTOR_IDC_LENGTH}
						placeholder='Cédula exacta de la contrata'
						onChange={onContractorIdcChange}
						onPressEnter={searchContractor}
						style={{ width: '100%', maxWidth: 300 }}
					/>
					<Button
						icon={<SearchOutlined />}
						loading={lookup.isSearching}
						disabled={!normalizeContractorIdc(contractorIdc)}
						onClick={searchContractor}
					>
						Consultar
					</Button>
				</Space>

				{lookup.error && <Alert showIcon type='error' message={lookup.error} />}

				{result && (
					<Space direction='vertical' size='middle' style={{ width: '100%' }}>
						<Descriptions size='small' bordered column={{ xs: 1, sm: 2 }}>
							<Descriptions.Item label='Contrata'>
								{result.contractor?.name || 'N/A'}
							</Descriptions.Item>
							<Descriptions.Item label='Cédula contrata'>
								{result.contractor?.idc || 'N/A'}
							</Descriptions.Item>
							<Descriptions.Item label='Administrador'>
								{result.admin?.name || 'N/A'}
							</Descriptions.Item>
							<Descriptions.Item label='Cédula administrador'>
								{result.admin?.idc || 'N/A'}
							</Descriptions.Item>
							<Descriptions.Item label='Correo primario'>
								<Text style={{ wordBreak: 'break-all' }}>
									{currentEmail || 'N/A'}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label='Estado onboarding'>
								<Tag
									color={getOnboardingStatusColor(result.onboarding?.status)}
								>
									{result.onboarding?.status || 'Sin estado'}
								</Tag>
							</Descriptions.Item>
							<Descriptions.Item label='Último estado de entrega'>
								{result.onboarding?.lastDeliveryStatus || 'Sin registro'}
							</Descriptions.Item>
							<Descriptions.Item label='Elegibilidad'>
								<Tag color={isEligible ? 'green' : 'red'}>
									{isEligible ? 'Elegible' : 'No elegible'}
								</Tag>
							</Descriptions.Item>
						</Descriptions>

						{result.onboarding?.reason && (
							<Alert
								showIcon
								type={isEligible ? 'info' : 'warning'}
								message={getOnboardingEligibilityMessage(
									result.onboarding.reason,
								)}
							/>
						)}

						{canRenderAction && (
							<Button
								type='primary'
								icon={<MailOutlined />}
								disabled={!isEligible}
								onClick={openModal}
								style={{ whiteSpace: 'normal', height: 'auto' }}
							>
								Cambiar correo y reenviar onboarding
							</Button>
						)}
					</Space>
				)}
			</Space>

			<Modal
				title='Cambiar correo y reenviar onboarding'
				visible={modal.visible}
				onCancel={closeModal}
				closable={!modal.isSubmitting}
				maskClosable={!modal.isSubmitting}
				keyboard={!modal.isSubmitting}
				footer={null}
				destroyOnClose
			>
				<Space direction='vertical' size='middle' style={{ width: '100%' }}>
					<Descriptions size='small' bordered column={1}>
						<Descriptions.Item label='Contrata'>
							{result?.contractor?.name || 'N/A'} ·{' '}
							{result?.contractor?.idc || 'N/A'}
						</Descriptions.Item>
						<Descriptions.Item label='Administrador'>
							{result?.admin?.name || 'N/A'} · {result?.admin?.idc || 'N/A'}
						</Descriptions.Item>
						<Descriptions.Item label='Correo primario actual'>
							<Text style={{ wordBreak: 'break-all' }}>
								{currentEmail || 'N/A'}
							</Text>
						</Descriptions.Item>
					</Descriptions>

					<label htmlFor='tenant-admin-new-email'>Nuevo correo</label>
					<Input
						id='tenant-admin-new-email'
						aria-label='Nuevo correo del administrador'
						type='email'
						value={modal.newEmail}
						maxLength={MAX_ADMIN_EMAIL_LENGTH}
						disabled={modal.isSubmitting}
						onChange={onNewEmailChange}
					/>
					{modal.newEmail && (
						<Text type={emailValidationError ? 'danger' : 'secondary'}>
							{emailValidationError || `Se usará: ${normalizedNewEmail}`}
						</Text>
					)}

					<Alert
						showIcon
						type='warning'
						message='Esta acción cambia el correo primario e invalida el enlace anterior.'
						description='El nuevo envío queda sujeto al procesamiento del proveedor de correo; su aceptación no confirma la entrega final.'
					/>

					<Checkbox
						checked={modal.confirmed}
						disabled={modal.isSubmitting}
						onChange={event =>
							setModal(current => ({
								...current,
								confirmed: event.target.checked,
								error: null,
							}))
						}
					>
						Confirmo que validé el nuevo correo y deseo invalidar el enlace
						anterior.
					</Checkbox>

					{modal.error && <Alert showIcon type='error' message={modal.error} />}

					<Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
						<Button disabled={modal.isSubmitting} onClick={closeModal}>
							Cancelar
						</Button>
						<Button
							type='primary'
							loading={modal.isSubmitting}
							disabled={
								Boolean(emailValidationError) || modal.confirmed !== true
							}
							onClick={submitReissue}
							style={{ whiteSpace: 'normal', height: 'auto' }}
						>
							Cambiar correo y reenviar onboarding
						</Button>
					</Space>
				</Space>
			</Modal>
		</Card>
	);
}
